import PreviewTable from 'components/PreviewTable';
import { WaterfallStructure } from 'further-types/lp/carry-settings';
import React from 'react';
import useWaterfallElements from '../hooks/useWaterfallElements';
import { numberToCurrencyString } from 'further-ui/utils';
import { CurrencyCode } from 'further-types/lp/vehicle';

const remainingToDistributeTooltip =
  'This is the total distributable amount minus any funds distributed to date. Where funds have been over or under-allocated to the carried interest partner, the limited partner amount will be adjusted to account for this.';

const columns = [
  {
    key: 'investorLabel',
    label: 'Investor',
  },
  {
    key: 'totalCommitment',
    label: 'Total commitment',
  },
  {
    key: 'paidInCapital',
    label: 'Paid in capital',
  },
  {
    key: 'grossProceeds',
    label: 'Gross proceeds',
  },
];

const splitColumns = [
  {
    key: 'cpProfitsSplit',
    label: 'Extra proceeds split',
    showDivider: true,
    tooltip:
      "This step is the amount of excess proceeds allocated to the carry partner based on the vehicle's carried interest rate of $rate%.",
  },
  {
    key: 'cpTotalDistributable',
    label: 'Total distributable',
  },
  {
    key: 'cpDistributedToDate',
    label: 'Distributed to date',
  },
  {
    key: 'cpRemainingToDistribute',
    label: 'Remaining to distribute',
    tooltip: remainingToDistributeTooltip,
  },
  {
    key: 'lpProfitsSplit',
    label: 'Extra proceeds split',
    showDivider: true,
    tooltip:
      "This step is the amount of excess proceeds allocated to the limited partners based on the vehicle's carried interest rate of $rate%.",
  },
  {
    key: 'lpTotalDistributable',
    label: 'Total distributable',
  },
  {
    key: 'lpDpi',
    label: 'DPI',
  },
  {
    key: 'lpDistributedToDate',
    label: 'Distributed to date',
  },
  {
    key: 'lpRemainingToDistribute',
    label: 'Remaining to distribute',
    tooltip: remainingToDistributeTooltip,
  },
];

type Props = {
  baseCurrency: CurrencyCode;
  fundsToDistribute: Array<{
    commitmentId: string;
    investorLabel: string;
    waterfall: {
      totalCommitment: number;
      paidInCapital: number;
      grossProceeds: number;
      preferredReturnAccrued: {
        accruedPreferredReturn: number;
      };
      catchUpAccrued: number;
      waterfallElements: Array<{
        element: WaterfallStructure;
        value: number;
      }>;
      capitalSplit: {
        carryPartner: {
          excessProceeds: number;
          remainingToDistribute: number;
          totalDistributable: number;
          distributedToDate: number;
        };
        limitedPartner: {
          excessProceeds: number;
          remainingToDistribute: number;
          totalDistributable: number;
          distributedToDate: number;
          dpi: number;
        };
      };
    };
  }>;
};

const WaterfallCalculations: React.FC<Props> = ({
  fundsToDistribute,
  baseCurrency,
}) => {
  const waterfallSteps = useWaterfallElements(fundsToDistribute, baseCurrency);

  const renderValue = (value: number) => {
    return numberToCurrencyString(value, {
      currency: baseCurrency,
    });
  };

  const rows = fundsToDistribute.map((commitmentDistribution) => {
    return {
      investorLabel: commitmentDistribution.investorLabel,
      totalCommitment: renderValue(
        commitmentDistribution.waterfall.totalCommitment,
      ),
      paidInCapital: renderValue(
        commitmentDistribution.waterfall.paidInCapital,
      ),
      grossProceeds: renderValue(
        commitmentDistribution.waterfall.grossProceeds,
      ),
      preferredReturnAccrued: renderValue(
        commitmentDistribution.waterfall.preferredReturnAccrued
          .accruedPreferredReturn,
      ),
      catchUpAccrued: renderValue(
        commitmentDistribution.waterfall.catchUpAccrued,
      ),
      ...waterfallSteps.getValues(commitmentDistribution.commitmentId),
      cpProfitsSplit: renderValue(
        commitmentDistribution.waterfall.capitalSplit.carryPartner
          .excessProceeds,
      ),
      cpTotalDistributable: renderValue(
        commitmentDistribution.waterfall.capitalSplit.carryPartner
          .totalDistributable,
      ),
      cpDistributedToDate: renderValue(
        commitmentDistribution.waterfall.capitalSplit.carryPartner
          .distributedToDate,
      ),
      cpRemainingToDistribute: renderValue(
        commitmentDistribution.waterfall.capitalSplit.carryPartner
          .remainingToDistribute,
      ),
      lpProfitsSplit: renderValue(
        commitmentDistribution.waterfall.capitalSplit.limitedPartner
          .excessProceeds,
      ),
      lpTotalDistributable: renderValue(
        commitmentDistribution.waterfall.capitalSplit.limitedPartner
          .totalDistributable,
      ),
      lpDpi: renderValue(
        commitmentDistribution.waterfall.capitalSplit.limitedPartner.dpi,
      ),
      lpDistributedToDate: renderValue(
        commitmentDistribution.waterfall.capitalSplit.limitedPartner
          .distributedToDate,
      ),
      lpRemainingToDistribute: renderValue(
        commitmentDistribution.waterfall.capitalSplit.limitedPartner
          .remainingToDistribute,
      ),
    };
  });

  return (
    <PreviewTable
      title={[
        {
          label: 'Waterfall calculation',
          colspan: columns.length + waterfallSteps.getColumns().length,
          background: 'default',
          align: 'center',
          size: 'sm',
        },
        {
          label: 'Carry partner',
          colspan: 4,
          background: 'default',
          align: 'center',
          size: 'sm',
        },
        {
          label: 'Limited partners',
          colspan: 5,
          background: 'default',
          align: 'center',
          size: 'sm',
        },
      ]}
      columns={[...columns, ...waterfallSteps.getColumns(), ...splitColumns]}
      rows={rows}
      footer={[]}
      background="white"
      scrollable
      preventHeadingWrap
    />
  );
};

export default WaterfallCalculations;
